<!-- 
  @component ❌
  @deprecated Use the `<Button />` component from `$mds/Buttons/Button` instead.
  @file apps/web/src/components/mds/Buttons/Button.svelte
  @ignore
-->
<script lang="ts">
  // Button state
  let className = '';
  export { className as class };
  export let type = 'button';
  export let div = false;
  export let disabled = false;
  export let loading = false;
  export let href: string | null = null;
  export let target = null;
  export let rel = null;
  export let style = '';

  // Classes which are always present
  const baseClasses =
    'appearance-none text-center whitespace-nowrap focus:outline-none transition-colors duration-200 disabled:pointer-events-none disabled:opacity-50';

  // Classes selected based on the value of the `kind` prop.
  const kindClasses = {
    primary: 'bg-action-ondark-base hover:bg-action-ondark-active text-onlight-secondary',
    secondary: 'bg-ondark-medium hover:bg-ondark-high text-ondark-primary',
    tertiary: 'bg-ondark-low text-ondark-primary',
    outline: 'hover:bg-ondark-low border border-ondark-strong text-ondark-primary',
    basic: 'bg-transparent text-ondark-primary',
    warning: 'bg-alert-ondark-base hover:bg-[#FF3D61] text-ondark-primary',
    dark: 'text-ondark-secondary border border-ondark-subtle hover:bg-ondark-medium',
    subtledark: 'bg-ondark-medium hover:bg-ondark-high text-white',
    light: 'bg-onlight-medium text-onlight-secondary'
  };

  // Classes selected based on the value of the `size` prop.
  const sizeClasses = {
    large: {
      height: 'h-14',
      padding: 'px-8',
      fontWeight: 'font-semibold',
      textSize: 'text-base'
    },
    // TODO: maybe rename it to large, and large to xLarge?
    'medium-large': {
      height: 'h-12',
      padding: 'px-5',
      fontWeight: 'font-medium',
      textSize: 'text-sm'
    },
    medium: {
      height: 'h-10',
      padding: 'px-8',
      fontWeight: 'font-medium',
      textSize: 'text-sm'
    },
    small: {
      height: 'h-8',
      padding: 'px-4',
      fontWeight: 'font-medium',
      textSize: 'text-xs'
    }
  };

  // Button style
  export let kind:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'outline'
    | 'basic'
    | 'warning'
    | 'dark'
    | 'light'
    | 'empty'
    | 'subtledark'
    | 'custom' = 'primary';
  export let size = '';

  const initialSize = size;
  const classFromPresetOr = (cls: string, otherwise: string): string =>
    Object.keys(sizeClasses).includes(initialSize) ? sizeClasses[initialSize][cls] : otherwise;
  size = classFromPresetOr('height', size);

  export let padding = classFromPresetOr('padding', 'px-6');
  export let border = '';
  export let rounded = 'rounded-lg';
  export let fontWeight = classFromPresetOr('fontWeight', 'font-medium');
  export let textSize = classFromPresetOr('textSize', 'text-sm');
  export let backgroundColor = kind === 'outline' ? 'bg-ondark-base' : '';

  $: fullClassName = `${baseClasses} ${
    kind !== 'custom' ? kindClasses[kind] : ''
  } ${padding} ${border} ${rounded} ${fontWeight} ${textSize} ${backgroundColor} ${size} ${className}`;
  $: isDisabled = disabled || loading;
</script>

{#if div}
  <div
    role={href ? 'link' : 'button'}
    class="inline-flex items-center justify-center {fullClassName}"
    class:spinner={loading}
    disabled={isDisabled}
    on:click
    on:mouseenter
    on:mouseleave
  >
    <slot />
  </div>
{:else if href}
  <a
    class="inline-flex items-center justify-center {fullClassName}"
    {href}
    {target}
    {rel}
    {style}
    {disabled}
    class:spinner={loading}
    on:click
    on:mouseenter
    on:mouseleave
  >
    <slot />
  </a>
{:else}
  <button
    {type}
    {style}
    class={fullClassName}
    class:spinner={loading}
    disabled={isDisabled}
    on:click
    on:mouseenter
    on:mouseleave
  >
    <slot />
  </button>
{/if}
